import React, { useEffect, useState, useMemo, useRef } from 'react';

import { FilterFixed, useFilterDataHook } from '@op/components/FilterCommon';

import FilterBoxCommon from '@op/components/FilterBoxCommon';

import { connect, useIntl, useLocation } from 'umi';
import CardCommon from '@op/components/CardCommon';
import { newsTimeSelectOptions } from '@op/components/FilterBoxCommon/constant';
import CardLoading from '@op/components/CardLoading';
import { useDownloadParams } from '@op/hooks/useDownloadParams';
import { useGetSingePageSourceRule } from '@op/hooks/useGetPageSourceRule';
import { useInitBuriedPointInfo } from '@op/operationLog/buriedPoint';
import { getSearchText } from '../../constants';
import {
  useRequestComments,
  useRequestKeyword,
  useRequestLatestEditLog,
  useRequestStats,
} from '../../hooks/useFeedsRequest';
import '@op/assets/scss/pages/feeds/NewMedia.scss';
import KeywordBox from '../../components/KeywordBox';
import { useNewsMediaColumns } from '@op/components/TableCommonHook/hooks/useNewsMediaColumns';
import { SortBy } from 'tea-component/lib/table/addons';
import TableCommonHook from '@op/components/TableCommonHook';
import { usePageInfo } from '@op/hooks/common';
import { filterIEGGPr } from '@op/pages/news/hooks/useNewsInfo';
import { useWindowSize } from '@op/hooks/useResize';
import { useFeedsGpt } from '../../hooks/useFeedsGpt';
import { DBO_TM } from '@op/utils/format';
import { beforeCacheDealwithData } from '../../constants/filter';

function NewMedia({ userInfo, gameInfo, wholeGameInfo, buriedPointInfo }: any) {
  const { Sentiment_Rating } = useGetSingePageSourceRule({
    keys: 'Sentiment_Rating',
  });
  const { download } = useDownloadParams();

  const {
    feedsComments,
    feedsCommentsLoading,
    requestComments,
    requestSearchComments,
    feedsSearchComments,
    feedsSearchCommentsLoading,
  } = useRequestComments({});
  const { state } = useLocation<{
    languageType: string;
    sort_item: string;
    search: string;
  }>();
  useInitBuriedPointInfo({ buriedPoint: buriedPointInfo });
  const [language, setLanguage] = useState(state?.languageType || 'origin');
  const [sortBy, setSortBy] = useState<SortBy<any>>({
    by: state?.sort_item || 'release_time',
    order: 'desc',
  });
  const [search, setSearch] = useState(state?.search || '');

  const {
    loading: initLoading,
    filter,
    changeFilter,
    filterOptions,
    filterCommonParams,
  } = useFilterDataHook({ pageName: 'Feeds_News_Media' });
  const { formatMessage } = useIntl();

  const { pageInfo, changePageInfo } = usePageInfo({
    defaultPageInfo: { page: 1, page_size: 50 },
  });

  const { feedsKeyword, feedsKeywordLoading, requestKeyword } =
    useRequestKeyword();
  const { feedsStats, feedsStatsLoading, requestState } = useRequestStats({});

  useEffect(() => {
    if (!userInfo || !gameInfo || !filter) return;

    requestState({
      ...filter,
      ...gameInfo,
      user_id: userInfo.userid,
      translated_to: 'origin',
    });

    requestKeyword({
      ...filter,
      ...gameInfo,
      user_id: userInfo.userid,
      keyword_sample_size: '500',
      translated_to: 'origin',
    });
  }, [userInfo, filter]);

  const [filterIds, setFilterIds] = useState<Array<string>>([]);

  const [comments, total, hasSearch, commentsList] = useMemo(() => {
    const hasSearch = search.trim().length > 0;
    const data = hasSearch ? feedsSearchComments : feedsComments;
    const list = (data?.news_detail_info || []).filter(
      (item: any) => !filterIds.includes(item.md5_uni),
    );
    const commentsList = (data?.news_detail_info || []).map((item: any) => {
      return {
        ...item,
        comment_uin: item.md5_uni,
      };
    });

    return [list, data?.total || 0, hasSearch, commentsList];
  }, [search, feedsSearchComments, feedsComments, filterIds]);

  const loading = hasSearch ? feedsSearchCommentsLoading : feedsCommentsLoading;

  const commonParams = useMemo(() => {
    const searchParams = hasSearch
      ? {
        search_data: search,
        sentiment: [],
        reviews_rating: [],
        social_media_by_keyword: null,
        social_media_by_account: null,
        sort_model: sortBy.order,
        sort_item: sortBy.by === 'release_time' ? '1' : '2',
      }
      : {};

    return {
      ...pageInfo,
      ...gameInfo,
      ...filter,
      match_model: 'exact', // TODO 其他地方获取
      translated_to: 'origin',
      sort_model: sortBy.order,
      sort_item: sortBy.by,
      ...searchParams,
    };
  }, [pageInfo, search, sortBy]);

  useEffect(() => {
    if (!gameInfo || !filter) return;
    const type = hasSearch ? requestSearchComments : requestComments;
    type(commonParams);
  }, [commonParams]);

  const isEmpty = !Array.isArray(comments) || comments.length === 0;

  const { GptBtnElement, GptElement } = useFeedsGpt({
    filter,
    commonParams,
    gameInfo,
    hasAdvancedSummary: false,
    wholeGameInfo,
    gptName: 'News Media',
    feedsStats
  });

  const { logCommentMap, loading: logLoading } = useRequestLatestEditLog({
    data: commentsList as any,
    otherParams: { type: 1 },
  });

  const { showIEGGPr, backCond, renderExcludeFilter } = filterIEGGPr();

  const { columns, sortColumns } = useNewsMediaColumns({
    language,
    total: hasSearch ? total : undefined,
    renderKey: hasSearch ? search : null,
    sort: sortBy,
    setSort: setSortBy,
    logCommentMap,
    logLoading,
    hasSearch,
    showIEGGPr,
    gameInfo,
    setLanguage,
  });

  const isFirst = useRef(true);

  const { windowSize } = useWindowSize();

  return (
    <div className="feeds-socialmedia  feeds-news-media-wrapper">
      <FilterFixed
        filter={filterOptions}
        operateShowOptions={['more', 'history', 'save']}
        confirm={(value: any) => {
          changeFilter(value);
          if (isFirst.current) {
            isFirst.current = false;
          } else {
            changePageInfo();
          }
        }}
        defaultConfig={{
          channel: {
            channelKey: 'news',
          },
          reviewers: {
            params: {
              channel_type: 'news',
            },
          },
        }}
        {...filterCommonParams}
        defaultValue={DBO_TM.utcFilterRangeDay(29, 0)}
        backCondOriginData={(value) => {
          backCond(value);
        }}
        renderExcludeFilter={renderExcludeFilter}
        beforeCacheDealwithData={beforeCacheDealwithData}
      ></FilterFixed>

      <CardLoading loading={initLoading}>
        {!hasSearch && (
          <CardCommon
            loading={feedsKeywordLoading || feedsStatsLoading}
            loadingHeight={200}
            className="social-media-keyword-box"
          >
            <KeywordBox
              keywords={feedsKeyword}
              data={feedsStats as any}
              tableTotal={total}
              commentLoading={feedsCommentsLoading}
              stabled
              keyMetricsKey="news"
              showIEGGPr={showIEGGPr}
              GptBtnElement={GptBtnElement}
            />
          </CardCommon>
        )}

        {GptElement}

        <FilterBoxCommon
          left={[
            {
              key: 'gpt',
              show: hasSearch,
              element: GptBtnElement,
            },
          ]}
          right={[
            {
              key: 'sortBy',
              defaultValue: sortBy.by,
              defaultParams: {
                options: newsTimeSelectOptions,
              },
              style: { marginRight: 10 },
              show: search.trim().length > 0,
            },
            {
              key: 'search',
              className: 'dbo-select-searchbox newmedia',
              defaultValue: search,
              defaultParams: {
                placeholder: formatMessage({
                  id: getSearchText({
                    isSearch: hasSearch,
                    text: 'Search news headline by original keyword or phrase',
                    width: windowSize.width,
                  }),
                }),
                className: 'dbo-search-input',
                onSearch: (value: any) => {
                  const hasValue = value.trim().length > 0; // 新的搜索值
                  const beforeHasValue = search.trim().length > 0; // 旧的搜索值

                  if (beforeHasValue && !hasValue) {
                    // 如果之前有，现在没有，前往无搜索状态
                    setSortBy({ order: 'desc', by: 'release_time' });
                  }

                  if (!beforeHasValue && hasValue) {
                    // 如果之前没有，现在有了：前往搜索状态
                    setSortBy({ order: 'desc', by: 'relevance' });
                  }
                  changePageInfo();
                  setSearch(value);
                },
                onClear: () => {
                  changePageInfo();
                  setSortBy({ order: 'desc', by: 'release_time' });
                  setSearch('');
                },
              },
            },
            {
              key: 'download',
              event: {
                onClick: () => {
                  if (!total) return;

                  const fileName = 'News Media Feeds';

                  const downloadFilter = {
                    ...filter,
                    ...gameInfo,
                    ...commonParams,
                    page: 1,
                    page_size: 1000,
                  };

                  download({
                    key: 'FeedsNewsMediaDownload',
                    fileName,
                    filter: downloadFilter,
                    auxiliaryParams: {
                      hideSentimentRating: Sentiment_Rating,
                      hasSearch,
                    },
                  });
                },
              },
            },
          ]}
          onChange={(value, type) => {
            if (type === 'language') {
              setLanguage(value.language);
            } else {
              setSortBy({ by: value.sortBy, order: 'desc' });
            }
          }}
        ></FilterBoxCommon>

        <CardCommon
          className="feeds-newmediabox-wrap"
          loading={loading}
          loadingHeight={300}
          empty={isEmpty}
          emptyHeight={300}
        >
          <TableCommonHook
            minWidth={1600}
            recordKey={'md5_uni'}
            tableSticky={true}
            records={comments || []}
            total={total || 0}
            pageInfo={pageInfo}
            setPageInfo={changePageInfo}
            columns={columns}
            sortColumns={sortColumns}
            sortInfo={[sortBy, setSortBy]}
          />
        </CardCommon>
      </CardLoading>
    </div>
  );
}

export default connect((state: any) => {
  const { loading, home, opinionCommon } = state;

  return {
    userInfoLoading: loading.effects['home/getUserInfo'],
    userInfo: home.userInfo,
    gameInfo: opinionCommon.gameInfo,
    wholeGameInfo: opinionCommon.wholeGameInfo,
  };
})(NewMedia);
